import { Component } from '@theme/component';

/**
 * The two behaviours the contact-form markup cannot carry on its own.
 *
 * 1. The order-number row is only worth asking for on order-related subjects.
 *    Which subjects those are is decided server-side — the options carrying
 *    `data-needs-order` — so the mapping lives next to the translated labels
 *    instead of as a list of English keys in here. The row ships visible and this
 *    hides it, which is the safe direction: with scripting off the field stays,
 *    rather than becoming unreachable. While hidden the input is disabled too, so
 *    a subject change does not leave an empty `contact[order]` in the merchant's
 *    notification e-mail.
 *
 * 2. Consent is gated by `required` on the checkbox, not by this file — it is a
 *    legal gate and has to hold with scripting off. All this does is trade the
 *    browser's validation bubble for the one-line message under the checkbox,
 *    which is server-rendered (and therefore translated) and only revealed here.
 *
 * `invalid` does not bubble, so that listener cannot go through the theme's
 * declarative `on:` bindings and is attached directly. The rest follow it for
 * consistency; an AbortController takes them all down together.
 *
 * Refs are read defensively rather than through `requiredRefs`: after a
 * successful post the section renders the confirmation card instead of the
 * fields, so none of them exist and `requiredRefs` would throw.
 *
 * @typedef {Object} ContactFormRefs
 * @property {HTMLSelectElement} [subject] - Subject picker
 * @property {HTMLElement} [orderRow] - Order-number field wrapper
 * @property {HTMLInputElement} [orderInput] - Order-number input
 * @property {HTMLInputElement} [consent] - Required consent checkbox
 * @property {HTMLElement} [consentError] - Server-rendered consent message
 *
 * @extends {Component<ContactFormRefs>}
 */
class DavittaContactForm extends Component {
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();

    const { subject, consent } = this.refs;

    if (!subject && !consent) return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    subject?.addEventListener('change', () => this.#syncOrderRow(), { signal });
    consent?.addEventListener('invalid', () => this.#showConsentError(), { signal });
    consent?.addEventListener('change', () => this.#clearConsentError(), { signal });

    this.#syncOrderRow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  #syncOrderRow() {
    const { subject, orderRow, orderInput } = this.refs;

    if (!subject || !orderRow) return;

    const needed = subject.selectedOptions[0]?.hasAttribute('data-needs-order') ?? false;

    orderRow.hidden = !needed;
    if (orderInput) orderInput.disabled = !needed;
  }

  #showConsentError() {
    const { consent, consentError } = this.refs;

    if (consentError) consentError.hidden = false;
    consent?.setAttribute('aria-invalid', 'true');
  }

  #clearConsentError() {
    const { consent, consentError } = this.refs;

    if (!consent?.checked) return;

    if (consentError) consentError.hidden = true;
    consent.removeAttribute('aria-invalid');
  }
}

// The '-component' suffix is what makes the element eligible for the theme's
// declarative bindings and its upgrade-race retry. Nothing here is bound
// declaratively today, but a mismatched tag name is a trap for whoever adds the
// first `on:` attribute.
if (!customElements.get('davitta-contact-form-component')) {
  customElements.define('davitta-contact-form-component', DavittaContactForm);
}
